import { DateTime } from 'luxon';
import { BeaconFunctionAppProperties } from '../../common/beacon.interfaces';
import { Badge, Form } from 'react-bootstrap';
import { useMemo, useState } from 'react';
import FavoriteSvg from '../../../common/components/svg/FavoriteSvg';
import EditPencilSvg from '../../../common/components/svg/EditPencilSvg';
import '../../styles/beacon.css'
import EyeSvg from '../../../common/components/svg/EyeSvg';
import GitHubSvg from '../../../common/components/svg/GitHubSvg';
import ArrowDownSvg from '../../../common/components/svg/ArrowDownSvg';

export default function BeaconFunctionAppTable(props: BeaconFunctionAppProperties) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleFocus = (id: string) => {
    setExpandedId(id); // Only one input can be expanded at a time
  };

  const handleBlur = (id: string) => {
    // Optional: Delay collapsing to allow clicking inside the textarea
    setTimeout(() => {
      if (expandedId === id) {
        setExpandedId(null);
      }
    }, 100); 
  };

  const tags: string[] = JSON.parse(props.beaconFunctionAppsRow?.tags ?? '[]')

  const view = useMemo(() => {
    return <div id={props.functionApp} className='beacon-card'>
        <div key={`${props.functionApp}-${props.type}`} className='beacon-function-app'>
          <h1>{props.functionApp} {<FavoriteSvg height={25} selected={props.beacons.some(b => b.isFavorite === true)}/>} <span data-functionApp={props.functionApp} onClick={props.handleEditFunctionApp} title={`Edit beacon settings for ${props.functionApp}`} style={{cursor: 'pointer'}}>{ <EditPencilSvg height={16} />}</span></h1>
          <div style={{padding: '2px'}}>
            <a className='beacon-link' href={`#top`}>
              <svg
                height="18"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg> top
            </a>&nbsp;&nbsp;&nbsp;&nbsp;
            <a className='beacon-link' target="_blank" rel="noopener noreferrer" href={`https://github.com/nhsllc/${props.functionApp}`}>
              <GitHubSvg height={18}/>
            </a>
            {tags.map(t => <><Badge bg='secondary'>{t}</Badge>&nbsp;&nbsp;</>)}
          </div>
          
          <div className='beacon-function'>
            <table key={`${props.functionApp}-beacon-table`} style={{width: '100%'}}>
              <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 10 }}>
                <tr key={`${props.functionApp}-beacon-table-headers`} style={{background: 'rgb(248, 248, 248)'}}>
                  <td>function</td>
                  <td>trigger</td>
                  <td>last start</td>
                  <td>last complete</td>
                  <td>since last complete</td>
                  <td>start to complete</td>
                  <td>fave</td>
                  <td>edit</td>
                </tr>
              </thead>
              <tbody>
                {
                  props.beacons.map(b => (
                    <>
                    <tr id={`${b.partitionKey}-${b.rowKey}`} key={`${props.functionApp}-${props.type}-${b.rowKey}`}>
                      <td>
                        <span className='beacon-header'>{b.rowKey}</span>
                      </td>
                      <td>
                        <span>{b.functionsRow?.functionTrigger}{b.functionsRow?.functionTrigger === 'timer' ? ` every ${b.functionsRow?.timerTimeInMinutes} minute(s)` : ''}</span>
                      </td>
                      <td>
                        {DateTime.fromISO(b.lastRun).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
                      </td>
                      <td>
                        {DateTime.fromISO(b.lastComplete).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
                      </td>
                      <td style={{fontWeight: 'lighter'}}>
                        <span>{b.sinceLastCompleteSeconds.toFixed(0)}s</span>&nbsp;&nbsp;&nbsp;
                        <span>{b.sinceLastCompleteMinutes.toFixed(2)}m</span>&nbsp;&nbsp;&nbsp;
                        <span>{b.sinceLastCompleteHours.toFixed(2)}h</span>&nbsp;&nbsp;&nbsp;
                        <span>{b.sinceLastCompleteDays.toFixed(2)}d</span>
                      </td>
                      <td style={{fontWeight: 'lighter'}}>
                        <span>{b.startToCompleteSeconds.toFixed(0)}s</span>&nbsp;&nbsp;&nbsp;
                        <span>{b.startToCompleteMinutes.toFixed(2)}m</span>&nbsp;&nbsp;&nbsp;
                        <span>{b.startToCompleteHours.toFixed(2)}h</span>&nbsp;&nbsp;&nbsp;
                        <span>{b.startToCompleteDays.toFixed(2)}d</span>
                      </td>
                      <td style={{cursor: 'pointer'}}>{b.isFavorite === true 
                        ? <div onClick={props.handleFavoriteClicked} data-functionapp={props.functionApp} data-func={b.rowKey} data-isfavorited='false'>{<FavoriteSvg height={25} selected={true}/>}</div> 
                        : <div onClick={props.handleFavoriteClicked} data-functionapp={props.functionApp} data-func={b.rowKey} data-isfavorited='true'>{<FavoriteSvg height={25} selected={false}/>}</div>}
                      </td>
                      <td style={{cursor: 'pointer'}}>
                        <span data-functionapp={props.functionApp} data-func={b.rowKey} onClick={props.handleEditFunction} title={`Edit beacon settings for ${b.rowKey}`} style={{cursor: 'pointer'}}>{ <EditPencilSvg height={16} />}</span>
                      </td>
                    </tr>
                    {(b.functionsRow && b.functionsRow?.tags && JSON.parse(b.functionsRow?.tags).length > 0) ? (
                      <tr>
                        <td colSpan={8} className='beacon-label'>
                          {JSON.parse(b.functionsRow?.tags).map(tag => (
                            <>
                              <Badge key={tag} bg='secondary'>{tag}</Badge>&nbsp;
                            </>
                          ))}
                        </td>
                      </tr>
                    ) : null}
                    {b.logsTableRow ? <tr key={`${props.functionApp}-${props.type}-${b.rowKey}-error`} >
                      <td style={{background: '#ffffffa1'}} colSpan={8}>
                        <div>
                          <span>Last on-call event @ <strong>{DateTime.fromISO(b.logsTableRow?.timestamp).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}</strong>&nbsp;&nbsp;&nbsp;</span>
                          <span>{DateTime.utc().diff(DateTime.fromISO(b.logsTableRow?.timestamp), 'seconds').seconds.toFixed(2)}s&nbsp;&nbsp;&nbsp;</span>
                          <span>{DateTime.utc().diff(DateTime.fromISO(b.logsTableRow?.timestamp), 'minutes').minutes.toFixed(2)}m&nbsp;&nbsp;&nbsp;</span>
                          <span>{DateTime.utc().diff(DateTime.fromISO(b.logsTableRow?.timestamp), 'hours').hours.toFixed(2)}h&nbsp;&nbsp;&nbsp;</span>
                          <span>{DateTime.utc().diff(DateTime.fromISO(b.logsTableRow?.timestamp), 'days').days.toFixed(2)}d&nbsp;&nbsp;&nbsp;</span>
                          <span title={'View the Azure content in the browser'} data-app={b.partitionKey} data-func={b.rowKey} data-blobpath={b.logsTableRow?.blobName} onClick={props.handleViewError} className='beacon-link'>
                            <EyeSvg height={16}/>
                          </span>
                          &nbsp;&nbsp;
                          <span title={'Download the Azure content'} data-app={b.partitionKey} data-func={b.rowKey} data-blobpath={b.logsTableRow?.blobName} onClick={props.handleDownloadError} className='beacon-link'>
                            <ArrowDownSvg height={16}/>
                          </span>
  
                          <Form.Control
                            as={expandedId === b.logsTableRow.rowKey ? "textarea" : "input"}
                            defaultValue={b.logsTableRow.details}
                            onFocus={() => handleFocus(b.logsTableRow.rowKey)}
                            onBlur={() => handleBlur(b.logsTableRow.rowKey)}
                          />
                        </div>
                      </td>
                    </tr>: ''}
                    </>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
  }, [props])

  return view
}